import { Component } from '@theme/component';

/**
 * Switches between collection tabs and scrolls the product row.
 *
 * Every panel is rendered server side, so switching is only a matter of toggling
 * `hidden` — no fetching and no layout shift between tabs.
 */
class CollectionTabs extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);

    for (const list of this.#lists) {
      list.addEventListener('scroll', this.#handleScroll, { passive: true });
    }

    window.addEventListener('resize', this.#handleScroll);

    this.#updateArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);

    for (const list of this.#lists) {
      list.removeEventListener('scroll', this.#handleScroll);
    }

    window.removeEventListener('resize', this.#handleScroll);
  }

  /** @returns {HTMLElement[]} The scrolling product list of every panel. */
  get #lists() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.collection-tabs__products')]);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-tab]');

    if (tab instanceof HTMLElement && tab.dataset.tab) {
      this.#select(tab.dataset.tab);
      return;
    }

    const arrow = target.closest('[data-scroll-next], [data-scroll-previous]');
    if (!(arrow instanceof HTMLElement)) return;

    this.#scroll(arrow.closest('.collection-tabs__scroller'), arrow.hasAttribute('data-scroll-next') ? 1 : -1);
  };

  /**
   * Moves focus between tabs with the arrow keys, as expected of a tablist.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-tab]')) return;

    const tabs = [...this.querySelectorAll('[data-tab]')];
    const current = tabs.indexOf(/** @type {Element} */ (target.closest('[data-tab]')));
    const offset = event.key === 'ArrowRight' ? 1 : -1;
    const next = tabs[(current + offset + tabs.length) % tabs.length];

    if (!(next instanceof HTMLElement) || !next.dataset.tab) return;

    event.preventDefault();
    this.#select(next.dataset.tab);
    next.focus();
  };

  #handleScroll = () => {
    this.#updateArrows();
  };

  /** @param {string} id - The block id of the tab to show. */
  #select(id) {
    for (const tab of this.querySelectorAll('[data-tab]')) {
      if (!(tab instanceof HTMLElement)) continue;

      const selected = tab.dataset.tab === id;

      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    }

    for (const panel of this.querySelectorAll('[data-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;

      panel.hidden = panel.dataset.panel !== id;
    }

    // A hidden panel has no width, so its arrows can only be measured once shown.
    this.#updateArrows();
  }

  /**
   * Scrolls a panel by one card.
   *
   * @param {Element | null} scroller - The panel's scrolling wrapper.
   * @param {number} direction - 1 to move forward, -1 to move back.
   */
  #scroll(scroller, direction) {
    const list = scroller?.querySelector('.collection-tabs__products');
    const card = list?.firstElementChild;

    if (!(list instanceof HTMLElement) || !(card instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;

    list.scrollBy({ left: (card.offsetWidth + gap) * direction, behavior: 'smooth' });
  }

  /** Shows each arrow only while there is room to scroll that way. */
  #updateArrows() {
    for (const list of this.#lists) {
      const scroller = list.closest('.collection-tabs__scroller');
      const previous = scroller?.querySelector('[data-scroll-previous]');
      const next = scroller?.querySelector('[data-scroll-next]');

      // `scrollLeft` is negative in right to left documents.
      const position = Math.abs(list.scrollLeft);
      const overflow = list.scrollWidth - list.clientWidth;

      if (previous instanceof HTMLElement) previous.hidden = overflow < 1 || position < 1;
      if (next instanceof HTMLElement) next.hidden = overflow < 1 || position >= overflow - 1;
    }
  }
}

if (!customElements.get('collection-tabs')) {
  customElements.define('collection-tabs', CollectionTabs);
}
